import * as THREE from 'three';
import {
  LineBasicNodeMaterial,
  MeshBasicNodeMaterial,
  StorageInstancedBufferAttribute,
  type WebGPURenderer,
} from 'three/webgpu';
import {
  Fn,
  If,
  attribute,
  float,
  instanceIndex,
  max,
  positionLocal,
  storage,
  uniform,
  uv,
  vec3,
  vec4,
  wgslFn,
} from 'three/tsl';
import type { ScrapeEmitter } from '../game/Racer';
import type { PerfGpuTimer } from '../debug/perf/GpuTimer';
import type { RendererMode } from './Renderer';

const PARTICLE_DENSITY_MULTIPLIER = 1;
const MAX_PARTICLES = 256 * PARTICLE_DENSITY_MULTIPLIER;
const MAX_ENGINE_PARTICLES = 384 * PARTICLE_DENSITY_MULTIPLIER;
const MAX_SCRAPE_MARKS = 512 * PARTICLE_DENSITY_MULTIPLIER;
const MAX_SCRAPE_SPARKS = 640 * PARTICLE_DENSITY_MULTIPLIER;
const DUST_POINT_SIZE_REFERENCE_PX = 300;
const ENGINE_POINT_SIZE_REFERENCE_PX = 260;
const WEBGPU_ENGINE_PLUME_CONES_ENABLED = false;

const makeNodeAttribute = attribute as any;
const makeFn = Fn as any;
const makeIf = If as any;
const makeFloat = float as any;
const makeInstanceIndex = instanceIndex as any;
const makeMax = max as any;
const makeNodePositionLocal = positionLocal as any;
const makeNodeUv = uv as any;
const makeStorage = storage as any;
const makeUniform = uniform as any;
const makeVec3 = vec3 as any;
const makeVec4 = vec4 as any;
const makeWgslFn = wgslFn as any;

interface BillboardUniforms {
  right: any;
  up: any;
  pixelToWorld: any;
  referencePixelScale: number;
}

interface WebGPUDustUniforms {
  edgeSoftness: any;
  dissolveStrength: any;
  dissolveStart: any;
  noiseScale: any;
  erosionFeather: any;
  puffContrast: any;
  puffStretch: any;
  fadeStart: any;
  fadePower: any;
}

type WebGPUBillboardMaterial = MeshBasicNodeMaterial & {
  userData: MeshBasicNodeMaterial['userData'] & {
    webgpuParticleBillboardUniforms?: BillboardUniforms;
    webgpuDustUniforms?: WebGPUDustUniforms;
  };
};

interface WebGPUParticleRuntime {
  renderer?: WebGPURenderer | null;
  gpuTimer?: PerfGpuTimer | null;
}

interface GpuParticleForces {
  verticalAccel: number;
  drag: number;
  sizeGrowth: number;
  shrinkStart?: number;
  shrinkEndScale?: number;
  fadeCore?: boolean;
}

interface GpuParticleSpawn {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  size: number;
  alpha: number;
  core: number;
}

const billboardRight = new THREE.Vector3();
const billboardUp = new THREE.Vector3();
const billboardViewportSize = new THREE.Vector2();
const plumeDirection = new THREE.Vector3();
const plumePosition = new THREE.Vector3();
const plumeQuaternion = new THREE.Quaternion();
const plumeYAxis = new THREE.Vector3(0, 1, 0);

export interface DustTrailConfig {
  onTrackSpawnRate: number;
  offTrackSpawnRate: number;
  speedReference: number;
  rearOffset: number;
  spread: number;
  yOffset: number;
  onTrackLifetimeSec: number;
  offTrackLifetimeSec: number;
  backwardSpeedMin: number;
  backwardSpeedJitter: number;
  lateralVelocityJitter: number;
  upwardVelocityMin: number;
  upwardVelocityJitter: number;
  gravity: number;
  drag: number;
  onTrackSizeMin: number;
  onTrackSizeMax: number;
  offTrackSizeMin: number;
  offTrackSizeMax: number;
  onTrackOpacity: number;
  offTrackOpacity: number;
  sizeGrowth: number;
  webgpuDissolveStrength: number;
  webgpuDissolveStart: number;
  webgpuEdgeSoftness: number;
  webgpuNoiseScale: number;
  webgpuEndScale: number;
  webgpuErosionFeather: number;
  webgpuPuffContrast: number;
  webgpuPuffStretch: number;
  webgpuFadeStart: number;
  webgpuFadePower: number;
}

export interface EngineTrailConfig {
  maxIntensity: number;
  idleThreshold: number;
  spawnRateBase: number;
  spawnRateIntensity: number;
  nozzleGlowEnabled: boolean;
  nozzleGlowLifeSec: number;
  nozzleGlowSizeBase: number;
  nozzleGlowSizeIntensity: number;
  nozzleGlowOpacityBase: number;
  nozzleGlowOpacityIntensity: number;
  sideJitterBase: number;
  sideJitterIntensity: number;
  verticalJitterBase: number;
  verticalJitterIntensity: number;
  rearSpeedBase: number;
  rearSpeedByWorldSpeed: number;
  rearSpeedByIntensity: number;
  rearSpeedJitter: number;
  spawnBackOffsetBase: number;
  spawnBackOffsetIntensity: number;
  sideVelocityJitter: number;
  verticalVelocityBase: number;
  verticalVelocityJitter: number;
  lifetimeSec: number;
  lifetimeJitterSec: number;
  sizeBase: number;
  sizeIntensity: number;
  sizeJitter: number;
  opacityBase: number;
  opacityIntensity: number;
  coreMin: number;
  coreJitter: number;
  upwardDrift: number;
  drag: number;
  sizeGrowth: number;
  webgpuPlumeEnabled: boolean;
  webgpuPlumeLengthBase: number;
  webgpuPlumeLengthIntensity: number;
  webgpuPlumeRadiusBase: number;
  webgpuPlumeRadiusIntensity: number;
  webgpuPlumeOpacity: number;
  webgpuPlumeFlicker: number;
}

export interface ScrapeMarksConfig {
  markSpawnRate: number;
  sparkSpawnRate: number;
  speedReference: number;
  markLifeMin: number;
  markLifeJitter: number;
  markPositionJitter: number;
  markYOffset: number;
  markHeadingJitter: number;
  markLengthBase: number;
  markLengthIntensity: number;
  markLengthJitter: number;
  markWidthBase: number;
  markWidthIntensity: number;
  markWidthJitter: number;
  markOpacityBase: number;
  markOpacityIntensity: number;
  markFade: number;
  sparkLifeMin: number;
  sparkLifeJitter: number;
  sparkLateralJitter: number;
  sparkRearJitter: number;
  sparkScatterBase: number;
  sparkScatterIntensity: number;
  sparkScatterSpeed: number;
  sparkRearVelocityMin: number;
  sparkRearVelocityJitter: number;
  sparkSideVelocityJitter: number;
  sparkYBase: number;
  sparkYJitter: number;
  sparkUpVelocityBase: number;
  sparkUpVelocityJitter: number;
  sparkUpVelocityIntensity: number;
  sparkSizeBase: number;
  sparkSizeIntensity: number;
  sparkSizeJitter: number;
  sparkOpacityBase: number;
  sparkOpacityIntensity: number;
  sparkHeatBase: number;
  sparkHeatJitter: number;
  sparkDrag: number;
  sparkGravity: number;
  sparkCooling: number;
  sparkTailBase: number;
  sparkTailSpeed: number;
}

export interface GroundStreaksConfig {
  minSpeed: number;
  spawnRateBase: number;
  spawnRateSpeed: number;
  lateralSpread: number;
  aheadSpread: number;
  lengthBase: number;
  lengthSpeed: number;
  lengthJitter: number;
  lifetimeBase: number;
  lifetimeSpeed: number;
  opacityBase: number;
  opacitySpeed: number;
  headYOffset: number;
  tailYOffset: number;
  tailOpacity: number;
}

export interface ParticlesConfig {
  dustTrail: DustTrailConfig;
  engineTrail: EngineTrailConfig;
  scrapeMarks: ScrapeMarksConfig;
  groundStreaks: GroundStreaksConfig;
}

export const DEFAULT_PARTICLES_CONFIG: ParticlesConfig = {
  dustTrail: {
    onTrackSpawnRate: 8,
    offTrackSpawnRate: 36,
    speedReference: 30,
    rearOffset: 1.6,
    spread: 0.95,
    yOffset: 0.035,
    onTrackLifetimeSec: 0.55,
    offTrackLifetimeSec: 0.62,
    backwardSpeedMin: 1,
    backwardSpeedJitter: 1.5,
    lateralVelocityJitter: 1,
    upwardVelocityMin: 0.035,
    upwardVelocityJitter: 0.08,
    gravity: 2.4,
    drag: 2.8,
    onTrackSizeMin: 3,
    onTrackSizeMax: 5,
    offTrackSizeMin: 9,
    offTrackSizeMax: 17,
    onTrackOpacity: 0.55,
    offTrackOpacity: 0.5,
    sizeGrowth: 0.35,
    webgpuDissolveStrength: 0.38,
    webgpuDissolveStart: 0.48,
    webgpuEdgeSoftness: 0.28,
    webgpuNoiseScale: 2.2,
    webgpuEndScale: 0.35,
    webgpuErosionFeather: 0.18,
    webgpuPuffContrast: 1.15,
    webgpuPuffStretch: 0.35,
    webgpuFadeStart: 0.42,
    webgpuFadePower: 1.7,
  },
  engineTrail: {
    maxIntensity: 2,
    idleThreshold: 0.02,
    spawnRateBase: 18,
    spawnRateIntensity: 46,
    nozzleGlowEnabled: true,
    nozzleGlowLifeSec: 0.045,
    nozzleGlowSizeBase: 2.2,
    nozzleGlowSizeIntensity: 2.8,
    nozzleGlowOpacityBase: 0.12,
    nozzleGlowOpacityIntensity: 0.22,
    sideJitterBase: 0.1,
    sideJitterIntensity: 0.16,
    verticalJitterBase: 0.06,
    verticalJitterIntensity: 0.08,
    rearSpeedBase: 7,
    rearSpeedByWorldSpeed: 0.035,
    rearSpeedByIntensity: 7,
    rearSpeedJitter: 2.5,
    spawnBackOffsetBase: 0.15,
    spawnBackOffsetIntensity: 0.2,
    sideVelocityJitter: 0.9,
    verticalVelocityBase: -0.3,
    verticalVelocityJitter: 0.45,
    lifetimeSec: 0.1,
    lifetimeJitterSec: 0.14,
    sizeBase: 1.2,
    sizeIntensity: 2.4,
    sizeJitter: 0.7,
    opacityBase: 0.06,
    opacityIntensity: 0.24,
    coreMin: 0.55,
    coreJitter: 0.45,
    upwardDrift: 0.35,
    drag: 0.9,
    sizeGrowth: 0.3,
    webgpuPlumeEnabled: false,
    webgpuPlumeLengthBase: 0.7,
    webgpuPlumeLengthIntensity: 1.55,
    webgpuPlumeRadiusBase: 0.12,
    webgpuPlumeRadiusIntensity: 0.2,
    webgpuPlumeOpacity: 0.22,
    webgpuPlumeFlicker: 0.18,
  },
  scrapeMarks: {
    markSpawnRate: 22,
    sparkSpawnRate: 260,
    speedReference: 55,
    markLifeMin: 34,
    markLifeJitter: 24,
    markPositionJitter: 0.22,
    markYOffset: 0.01,
    markHeadingJitter: 0.22,
    markLengthBase: 0.85,
    markLengthIntensity: 1.15,
    markLengthJitter: 0.45,
    markWidthBase: 0.08,
    markWidthIntensity: 0.06,
    markWidthJitter: 0.04,
    markOpacityBase: 0.32,
    markOpacityIntensity: 0.26,
    markFade: 0.72,
    sparkLifeMin: 0.14,
    sparkLifeJitter: 0.24,
    sparkLateralJitter: 0.42,
    sparkRearJitter: 0.5,
    sparkScatterBase: 1,
    sparkScatterIntensity: 2.8,
    sparkScatterSpeed: 0.028,
    sparkRearVelocityMin: 1.1,
    sparkRearVelocityJitter: 1.6,
    sparkSideVelocityJitter: 2.1,
    sparkYBase: 0.12,
    sparkYJitter: 0.12,
    sparkUpVelocityBase: 1.8,
    sparkUpVelocityJitter: 3.2,
    sparkUpVelocityIntensity: 1.2,
    sparkSizeBase: 0.45,
    sparkSizeIntensity: 0.65,
    sparkSizeJitter: 0.45,
    sparkOpacityBase: 0.56,
    sparkOpacityIntensity: 0.4,
    sparkHeatBase: 0.75,
    sparkHeatJitter: 0.25,
    sparkDrag: 2.8,
    sparkGravity: 8.5,
    sparkCooling: 3.4,
    sparkTailBase: 1.2,
    sparkTailSpeed: 0.045,
  },
  groundStreaks: {
    minSpeed: 0.5,
    spawnRateBase: 24,
    spawnRateSpeed: 96,
    lateralSpread: 6,
    aheadSpread: 1.5,
    lengthBase: 1.2,
    lengthSpeed: 6.5,
    lengthJitter: 1.5,
    lifetimeBase: 0.18,
    lifetimeSpeed: 0.18,
    opacityBase: 0.45,
    opacitySpeed: 0.45,
    headYOffset: 0.06,
    tailYOffset: 0.05,
    tailOpacity: 0.05,
  },
};

type PartialConfig<T extends object> = Partial<T> | Record<string, unknown> | undefined;

function mergeParticleConfig<T extends object>(defaults: T, override: PartialConfig<T>): T {
  return { ...defaults, ...(override ?? {}) } as T;
}

interface Particle {
  age: number;
  life: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  size: number;
  alpha: number;
  random?: number;
}

interface EngineParticle extends Particle {
  core: number;
}

interface SparkParticle extends Particle {
  heat: number;
}

interface ScrapeMark {
  age: number;
  life: number;
  x: number;
  /** Track surface Y at spawn — marks stay where they were laid as the racer moves on. */
  y: number;
  z: number;
  heading: number;
  length: number;
  width: number;
  alpha: number;
}

const dustParticleNode = makeWgslFn(`
fn dust_particle(
  texCoord: vec2<f32>,
  alphaInput: f32,
  ageInput: f32,
  randomInput: f32,
  edgeSoftness: f32,
  dissolveStrength: f32,
  dissolveStart: f32,
  noiseScale: f32,
  erosionFeather: f32,
  puffContrast: f32,
  puffStretch: f32,
  fadeStart: f32,
  fadePower: f32
) -> vec4<f32> {
  let centered = texCoord - vec2<f32>(0.5, 0.5);
  let angle = randomInput * 6.28318530718;
  let ca = cos(angle);
  let sa = sin(angle);
  let stretch = mix(1.0 - puffStretch, 1.0 + puffStretch, fract(randomInput * 5.137));
  let p = vec2<f32>(
    (centered.x * ca - centered.y * sa) * stretch,
    (centered.x * sa + centered.y * ca) / max(0.001, stretch)
  );
  let d = length(p);
  let softEdge = 1.0 - smoothstep(edgeSoftness, 0.5, d);
  let wobble = 0.5 + 0.5 * sin((p.x * 2.3 + p.y * 1.7 + randomInput * 3.9 + ageInput * 0.35) * noiseScale);
  let lobeA = 1.0 - smoothstep(0.0, 0.34, length(p - vec2<f32>(0.11, -0.05)));
  let lobeB = 1.0 - smoothstep(0.0, 0.30, length(p - vec2<f32>(-0.16, 0.08)));
  let lobeC = 1.0 - smoothstep(0.0, 0.26, length(p - vec2<f32>(0.02, 0.18)));
  let puffBody = clamp(softEdge * (0.54 + (lobeA + lobeB + lobeC) * 0.24), 0.0, 1.0);
  let puffMask = pow(puffBody, max(0.1, puffContrast));
  let erosionMask = clamp(puffMask * 0.78 + wobble * 0.22, 0.0, 1.0);
  let dissolveAge = clamp((ageInput - dissolveStart) / max(0.001, 1.0 - dissolveStart), 0.0, 1.0);
  let cutoff = dissolveAge * dissolveStrength;
  let erode = smoothstep(cutoff, cutoff + max(0.02, erosionFeather), erosionMask);
  let warmCore = vec3<f32>(0.66, 0.42, 0.23);
  let coolEdge = vec3<f32>(0.34, 0.25, 0.18);
  let color = mix(coolEdge, warmCore, clamp(puffBody * 0.8 + wobble * 0.2, 0.0, 1.0));
  let birthFade = smoothstep(0.0, 0.08, ageInput);
  let deathT = clamp((ageInput - fadeStart) / max(0.001, 1.0 - fadeStart), 0.0, 1.0);
  let deathFade = pow(1.0 - smoothstep(0.0, 1.0, deathT), max(0.1, fadePower));
  let alpha = puffMask * erode * birthFade * deathFade * alphaInput;
  return vec4<f32>(color, alpha);
}
`);

const engineParticleNode = makeWgslFn(`
fn engine_particle(texCoord: vec2<f32>, alphaInput: f32, coreInput: f32) -> vec4<f32> {
  let centered = texCoord - vec2<f32>(0.5, 0.5);
  let d = length(centered);
  let coreMask = smoothstep(0.18, 0.02, d);
  let glow = smoothstep(0.5, 0.08, d);
  let outer = vec3<f32>(0.05, 0.34, 1.0);
  let inner = vec3<f32>(0.65, 0.95, 1.0);
  let color = min(mix(outer, inner, max(coreMask, coreInput)), vec3<f32>(1.4, 1.4, 1.4));
  let alpha = (glow * 0.42 + coreMask * 0.9) * alphaInput;
  return vec4<f32>(color, alpha);
}
`);

const scrapeMarkNode = makeWgslFn(`
fn scrape_mark(alphaInput: f32) -> vec4<f32> {
  return vec4<f32>(0.06, 0.045, 0.035, alphaInput);
}
`);

const scrapeSparkColorNode = makeWgslFn(`
fn scrape_spark_color(heat: f32) -> vec3<f32> {
  let ember = vec3<f32>(1.0, 0.34, 0.04);
  let hot = vec3<f32>(1.0, 0.88, 0.28);
  return mix(ember, hot, heat);
}
`);

const groundStreakNode = makeWgslFn(`
fn ground_streak(alphaInput: f32) -> vec4<f32> {
  return vec4<f32>(0.95, 0.92, 0.82, alphaInput);
}
`);

function makeWebGPUDustMaterial(
  gpuBillboard: boolean,
  gpuCompute = false,
  config = DEFAULT_PARTICLES_CONFIG.dustTrail,
): WebGPUBillboardMaterial {
  const material = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: true,
  }) as WebGPUBillboardMaterial;
  if (gpuBillboard) installGpuBillboardPositionNode(material, gpuCompute);
  const edgeSoftness = makeUniform(config.webgpuEdgeSoftness);
  const dissolveStrength = makeUniform(config.webgpuDissolveStrength);
  const dissolveStart = makeUniform(config.webgpuDissolveStart);
  const noiseScale = makeUniform(config.webgpuNoiseScale);
  const erosionFeather = makeUniform(config.webgpuErosionFeather);
  const puffContrast = makeUniform(config.webgpuPuffContrast);
  const puffStretch = makeUniform(config.webgpuPuffStretch);
  const fadeStart = makeUniform(config.webgpuFadeStart);
  const fadePower = makeUniform(config.webgpuFadePower);
  const particle = dustParticleNode({
    texCoord: makeNodeUv(),
    alphaInput: gpuCompute ? makeNodeAttribute('iRenderState').y : makeNodeAttribute('iAlpha'),
    ageInput: gpuCompute ? makeNodeAttribute('iRenderState').w : makeNodeAttribute('iAge'),
    randomInput: gpuCompute ? makeNodeAttribute('iRenderState').z : makeNodeAttribute('iRandom'),
    edgeSoftness,
    dissolveStrength,
    dissolveStart,
    noiseScale,
    erosionFeather,
    puffContrast,
    puffStretch,
    fadeStart,
    fadePower,
  });
  material.colorNode = particle.rgb;
  material.opacityNode = particle.a;
  material.userData.webgpuDustUniforms = {
    edgeSoftness,
    dissolveStrength,
    dissolveStart,
    noiseScale,
    erosionFeather,
    puffContrast,
    puffStretch,
    fadeStart,
    fadePower,
  };
  return material;
}

function makeWebGPUEngineTrailMaterial(gpuBillboard: boolean, gpuCompute = false): WebGPUBillboardMaterial {
  const material = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    toneMapped: false,
  }) as WebGPUBillboardMaterial;
  if (gpuBillboard) installGpuBillboardPositionNode(material, gpuCompute, ENGINE_POINT_SIZE_REFERENCE_PX);
  const particle = engineParticleNode({
    texCoord: makeNodeUv(),
    alphaInput: gpuCompute ? makeNodeAttribute('iRenderState').y : makeNodeAttribute('iAlpha'),
    coreInput: gpuCompute ? makeNodeAttribute('iRenderState').z : makeNodeAttribute('iCore'),
  });
  material.colorNode = particle.rgb;
  material.opacityNode = particle.a;
  return material;
}

function makeWebGPUScrapeMarkMaterial(): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    toneMapped: true,
  });
  const mark = scrapeMarkNode({ alphaInput: makeNodeAttribute('aAlpha') });
  material.colorNode = mark.rgb;
  material.opacityNode = mark.a;
  return material;
}

function makeWebGPUScrapeSparkMaterial(): LineBasicNodeMaterial {
  const material = new LineBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  material.colorNode = scrapeSparkColorNode({ heat: makeNodeAttribute('aHeat') });
  material.opacityNode = makeNodeAttribute('aAlpha');
  return material;
}

function makeWebGPUGroundStreakMaterial(): LineBasicNodeMaterial {
  const material = new LineBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const streak = groundStreakNode({ alphaInput: makeNodeAttribute('aAlpha') });
  material.colorNode = streak.rgb;
  material.opacityNode = streak.a;
  return material;
}

function makeWebGPUPlumeMaterial(): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: new THREE.Color(0x6bdcff),
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
}

function makeBillboardGeometry(
  _maxCount: number,
  positions: Float32Array,
  sizes: Float32Array,
  alphas: Float32Array,
  cores?: Float32Array,
  ages?: Float32Array,
  randoms?: Float32Array,
): THREE.PlaneGeometry {
  const geometry = new THREE.PlaneGeometry(1, 1, 1, 1);
  geometry.setAttribute('iPosition', new THREE.InstancedBufferAttribute(positions, 3));
  geometry.setAttribute('iSize', new THREE.InstancedBufferAttribute(sizes, 1));
  geometry.setAttribute('iAlpha', new THREE.InstancedBufferAttribute(alphas, 1));
  if (cores) geometry.setAttribute('iCore', new THREE.InstancedBufferAttribute(cores, 1));
  if (ages) geometry.setAttribute('iAge', new THREE.InstancedBufferAttribute(ages, 1));
  if (randoms) geometry.setAttribute('iRandom', new THREE.InstancedBufferAttribute(randoms, 1));
  return geometry;
}

function makeComputeBillboardGeometry(updater: GpuParticleUpdater): THREE.PlaneGeometry {
  const geometry = new THREE.PlaneGeometry(1, 1, 1, 1);
  geometry.setAttribute('iPositionLife', updater.positionLifeAttribute);
  geometry.setAttribute('iRenderState', updater.renderStateAttribute);
  return geometry;
}

function installGpuBillboardPositionNode(
  material: WebGPUBillboardMaterial,
  gpuCompute = false,
  referencePixelScale = DUST_POINT_SIZE_REFERENCE_PX,
): void {
  const right = makeUniform(new THREE.Vector3(1, 0, 0));
  const up = makeUniform(new THREE.Vector3(0, 1, 0));
  const pixelToWorld = makeUniform(1);
  const center = gpuCompute ? makeNodeAttribute('iPositionLife').xyz : makeNodeAttribute('iPosition');
  const size = gpuCompute ? makeNodeAttribute('iRenderState').x : makeNodeAttribute('iSize');
  const local = makeNodePositionLocal;
  const worldSize = size.mul(pixelToWorld);
  material.positionNode = center.add(
    right.mul(local.x).add(up.mul(local.y)).mul(worldSize),
  );
  material.userData.webgpuParticleBillboardUniforms = { right, up, pixelToWorld, referencePixelScale };
}

function particlePixelToWorldScale(
  renderer: { getDrawingBufferSize: (target: THREE.Vector2) => THREE.Vector2 },
  camera: THREE.Camera,
  referencePixelScale: number,
): number {
  renderer.getDrawingBufferSize(billboardViewportSize);
  const viewportHeight = Math.max(1, billboardViewportSize.y);
  const focalLengthPx = Math.max(1, 0.5 * viewportHeight * Math.abs(camera.projectionMatrix.elements[5] || 1));
  return referencePixelScale / focalLengthPx;
}

function gpuParticleBillboardsEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search);
  return !params.has('particlesCpu') && params.get('particles') !== 'cpu';
}

function gpuParticleComputeEnabled(runtime?: WebGPUParticleRuntime): runtime is WebGPUParticleRuntime & { renderer: WebGPURenderer } {
  if (!runtime?.renderer) return false;
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search);
  return !params.has('particlesCpu') && params.get('particles') !== 'cpu';
}

function gpuDustComputeEnabled(runtime?: WebGPUParticleRuntime): runtime is WebGPUParticleRuntime & { renderer: WebGPURenderer } {
  if (!gpuParticleComputeEnabled(runtime)) return false;
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search);
  return params.get('dustCompute') === '1';
}

function installBillboardBasisSync(
  mesh: THREE.InstancedMesh,
  material: WebGPUBillboardMaterial,
): void {
  const uniforms = material.userData.webgpuParticleBillboardUniforms;
  if (!uniforms) return;
  mesh.onBeforeRender = (_renderer, _scene, camera) => {
    const elements = camera.matrixWorld.elements;
    billboardRight.set(elements[0], elements[1], elements[2]);
    billboardUp.set(elements[4], elements[5], elements[6]);
    uniforms.right.value.copy(billboardRight);
    uniforms.up.value.copy(billboardUp);
    uniforms.pixelToWorld.value = particlePixelToWorldScale(_renderer, camera, uniforms.referencePixelScale);
  };
}

function installBillboardSync(
  mesh: THREE.InstancedMesh,
  positions: Float32Array,
  sizes: Float32Array,
  referencePixelScale = DUST_POINT_SIZE_REFERENCE_PX,
): void {
  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const scale = new THREE.Vector3();
  mesh.onBeforeRender = (_renderer, _scene, camera) => {
    const pixelToWorld = particlePixelToWorldScale(_renderer, camera, referencePixelScale);
    for (let i = 0; i < mesh.count; i++) {
      position.set(positions[i * 3 + 0], positions[i * 3 + 1], positions[i * 3 + 2]);
      scale.setScalar(sizes[i] * pixelToWorld);
      matrix.compose(position, camera.quaternion, scale);
      mesh.setMatrixAt(i, matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  };
}

class GpuParticleUpdater {
  readonly positionLife: Float32Array;
  readonly velocityAge: Float32Array;
  readonly baseState: Float32Array;
  readonly renderState: Float32Array;
  readonly positionLifeAttribute: StorageInstancedBufferAttribute;
  readonly velocityAgeAttribute: StorageInstancedBufferAttribute;
  readonly baseStateAttribute: StorageInstancedBufferAttribute;
  readonly renderStateAttribute: StorageInstancedBufferAttribute;

  private readonly positionLifeNode: any;
  private readonly velocityAgeNode: any;
  private readonly baseStateNode: any;
  private readonly renderStateNode: any;
  private readonly dtUniform = makeUniform(0);
  private readonly verticalAccelUniform: any;
  private readonly dragUniform: any;
  private readonly sizeGrowthUniform: any;
  private readonly shrinkStartUniform: any;
  private readonly shrinkEndScaleUniform: any;
  private readonly fadeCore: boolean;
  private readonly computeNode: any;
  private readonly activeSlots: number[] = [];
  private readonly activeIndex: Int32Array;
  private readonly freeSlots: number[] = [];
  private readonly ages: Float32Array;
  private readonly lives: Float32Array;
  private highWater = 0;
  private computeAvailable = true;

  constructor(
    readonly maxCount: number,
    private readonly renderer: WebGPURenderer,
    private readonly gpuTimer: PerfGpuTimer | null,
    private readonly label: string,
    forces: GpuParticleForces,
  ) {
    this.positionLife = new Float32Array(maxCount * 4);
    this.velocityAge = new Float32Array(maxCount * 4);
    this.baseState = new Float32Array(maxCount * 4);
    this.renderState = new Float32Array(maxCount * 4);
    this.positionLifeAttribute = makeStorageAttribute(this.positionLife);
    this.velocityAgeAttribute = makeStorageAttribute(this.velocityAge);
    this.baseStateAttribute = makeStorageAttribute(this.baseState);
    this.renderStateAttribute = makeStorageAttribute(this.renderState);
    this.positionLifeNode = makeStorage(this.positionLifeAttribute, 'vec4', maxCount).setName(`${label}_positionLife`);
    this.velocityAgeNode = makeStorage(this.velocityAgeAttribute, 'vec4', maxCount).setName(`${label}_velocityAge`);
    this.baseStateNode = makeStorage(this.baseStateAttribute, 'vec4', maxCount).setName(`${label}_baseState`);
    this.renderStateNode = makeStorage(this.renderStateAttribute, 'vec4', maxCount).setName(`${label}_renderState`);
    this.verticalAccelUniform = makeUniform(forces.verticalAccel);
    this.dragUniform = makeUniform(forces.drag);
    this.sizeGrowthUniform = makeUniform(forces.sizeGrowth);
    this.shrinkStartUniform = makeUniform(forces.shrinkStart ?? 1);
    this.shrinkEndScaleUniform = makeUniform(forces.shrinkEndScale ?? 1);
    this.fadeCore = forces.fadeCore ?? true;
    this.activeIndex = new Int32Array(maxCount);
    this.activeIndex.fill(-1);
    this.ages = new Float32Array(maxCount);
    this.lives = new Float32Array(maxCount);
    this.computeNode = this.createComputeNode();
  }

  get activeCount(): number {
    return this.activeSlots.length;
  }

  get drawCount(): number {
    return this.highWater;
  }

  spawn(spawn: GpuParticleSpawn): void {
    const slot = this.allocateSlot();
    if (slot < 0) return;
    const offset = slot * 4;
    this.positionLife[offset + 0] = spawn.x;
    this.positionLife[offset + 1] = spawn.y;
    this.positionLife[offset + 2] = spawn.z;
    this.positionLife[offset + 3] = spawn.life;
    this.velocityAge[offset + 0] = spawn.vx;
    this.velocityAge[offset + 1] = spawn.vy;
    this.velocityAge[offset + 2] = spawn.vz;
    this.velocityAge[offset + 3] = 0;
    this.baseState[offset + 0] = spawn.size;
    this.baseState[offset + 1] = spawn.alpha;
    this.baseState[offset + 2] = spawn.core;
    this.baseState[offset + 3] = 1;
    this.renderState[offset + 0] = spawn.size;
    this.renderState[offset + 1] = spawn.alpha;
    this.renderState[offset + 2] = spawn.core;
    this.renderState[offset + 3] = 1;
    this.ages[slot] = 0;
    this.lives[slot] = spawn.life;
    markStorageRange(this.positionLifeAttribute, offset, 4);
    markStorageRange(this.velocityAgeAttribute, offset, 4);
    markStorageRange(this.baseStateAttribute, offset, 4);
    markStorageRange(this.renderStateAttribute, offset, 4);
  }

  update(dt: number): void {
    if (this.activeSlots.length === 0) return;
    if (!this.computeAvailable) {
      this.updateCpu(dt);
      return;
    }

    this.dtUniform.value = dt;
    try {
      const run = (): void => { void this.renderer.compute(this.computeNode); };
      if (this.gpuTimer?.measureCompute) this.gpuTimer.measureCompute(`gpu.compute.particles.${this.label}`, run);
      else run();
      this.ageMetadata(dt);
    } catch (err) {
      this.computeAvailable = false;
      console.warn(`[particles] WebGPU compute failed for ${this.label}; falling back to CPU storage updates`, err);
      this.updateCpu(dt);
    }
  }

  clear(): void {
    if (this.highWater > 0) {
      this.positionLife.fill(0, 0, this.highWater * 4);
      this.velocityAge.fill(0, 0, this.highWater * 4);
      this.baseState.fill(0, 0, this.highWater * 4);
      this.renderState.fill(0, 0, this.highWater * 4);
      markStorageRange(this.positionLifeAttribute, 0, this.highWater * 4);
      markStorageRange(this.velocityAgeAttribute, 0, this.highWater * 4);
      markStorageRange(this.baseStateAttribute, 0, this.highWater * 4);
      markStorageRange(this.renderStateAttribute, 0, this.highWater * 4);
    }
    this.activeSlots.length = 0;
    this.freeSlots.length = 0;
    this.activeIndex.fill(-1);
    this.ages.fill(0);
    this.lives.fill(0);
    this.highWater = 0;
  }

  private allocateSlot(): number {
    const slot = this.freeSlots.pop() ?? (this.highWater < this.maxCount ? this.highWater++ : -1);
    if (slot < 0) return -1;
    this.activeIndex[slot] = this.activeSlots.length;
    this.activeSlots.push(slot);
    return slot;
  }

  private freeSlot(slot: number): void {
    const index = this.activeIndex[slot];
    if (index < 0) return;
    const last = this.activeSlots.pop()!;
    if (index < this.activeSlots.length) {
      this.activeSlots[index] = last;
      this.activeIndex[last] = index;
    }
    this.activeIndex[slot] = -1;
    this.freeSlots.push(slot);
    this.lives[slot] = 0;
  }

  private ageMetadata(dt: number): void {
    for (let i = this.activeSlots.length - 1; i >= 0; i--) {
      const slot = this.activeSlots[i];
      this.ages[slot] += dt;
      if (this.ages[slot] >= this.lives[slot]) this.freeSlot(slot);
    }
  }

  private updateCpu(dt: number): void {
    for (let i = this.activeSlots.length - 1; i >= 0; i--) {
      const slot = this.activeSlots[i];
      const offset = slot * 4;
      const life = this.positionLife[offset + 3];
      const age = this.ages[slot] + dt;
      this.ages[slot] = age;
      if (life <= 0 || age >= life) {
        this.renderState[offset + 0] = 0;
        this.renderState[offset + 1] = 0;
        this.renderState[offset + 2] = 0;
        this.renderState[offset + 3] = 0;
        this.velocityAge[offset + 3] = age;
        markStorageRange(this.velocityAgeAttribute, offset, 4);
        markStorageRange(this.renderStateAttribute, offset, 4);
        this.freeSlot(slot);
        continue;
      }
      const damping = Math.max(0, 1 - this.dragUniform.value * dt);
      this.velocityAge[offset + 0] *= damping;
      this.velocityAge[offset + 1] += this.verticalAccelUniform.value * dt;
      this.velocityAge[offset + 2] *= damping;
      this.velocityAge[offset + 3] = age;
      this.positionLife[offset + 0] += this.velocityAge[offset + 0] * dt;
      this.positionLife[offset + 1] += this.velocityAge[offset + 1] * dt;
      this.positionLife[offset + 2] += this.velocityAge[offset + 2] * dt;
      const t = age / Math.max(0.0001, life);
      const shrinkT = Math.max(0, Math.min(1, (t - this.shrinkStartUniform.value) / Math.max(0.0001, 1 - this.shrinkStartUniform.value)));
      const sizeScale = (1 + t * this.sizeGrowthUniform.value) * (1 + shrinkT * (this.shrinkEndScaleUniform.value - 1));
      this.renderState[offset + 0] = this.baseState[offset + 0] * sizeScale;
      this.renderState[offset + 1] = this.baseState[offset + 1] * (1 - t);
      this.renderState[offset + 2] = this.fadeCore ? this.baseState[offset + 2] * (1 - t) : this.baseState[offset + 2];
      this.renderState[offset + 3] = t;
      markStorageRange(this.positionLifeAttribute, offset, 4);
      markStorageRange(this.velocityAgeAttribute, offset, 4);
      markStorageRange(this.renderStateAttribute, offset, 4);
    }
  }

  private createComputeNode(): any {
    const positionLife = this.positionLifeNode;
    const velocityAge = this.velocityAgeNode;
    const baseState = this.baseStateNode;
    const renderState = this.renderStateNode;
    const dt = this.dtUniform;
    const verticalAccel = this.verticalAccelUniform;
    const drag = this.dragUniform;
    const sizeGrowth = this.sizeGrowthUniform;
    const shrinkStart = this.shrinkStartUniform;
    const shrinkEndScale = this.shrinkEndScaleUniform;
    const fadeCore = this.fadeCore;
    return makeFn(() => {
      const idx = makeInstanceIndex;
      const pl = positionLife.element(idx).toVar();
      const va = velocityAge.element(idx).toVar();
      const base = baseState.element(idx);
      const kill = () => {
        renderState.element(idx).assign(makeVec4(0, 0, 0, 0));
      };
      makeIf(pl.w.lessThanEqual(0), kill).Else(() => {
        const age = va.w.add(dt).toVar();
        makeIf(age.greaterThanEqual(pl.w), () => {
          velocityAge.element(idx).assign(makeVec4(va.xyz, age));
          kill();
        }).Else(() => {
          const damping = makeMax(0, makeFloat(1).sub(drag.mul(dt)));
          const nextVelocity = makeVec3(
            va.x.mul(damping),
            va.y.add(verticalAccel.mul(dt)),
            va.z.mul(damping),
          );
          const nextPosition = pl.xyz.add(nextVelocity.mul(dt));
          const t = age.div(makeMax(pl.w, 0.0001));
          const shrinkT = makeMax(0, t.sub(shrinkStart).div(makeMax(0.0001, makeFloat(1).sub(shrinkStart))));
          const sizeScale = makeFloat(1)
            .add(t.mul(sizeGrowth))
            .mul(makeFloat(1).add(shrinkT.mul(shrinkEndScale.sub(makeFloat(1)))));
          positionLife.element(idx).assign(makeVec4(nextPosition, pl.w));
          velocityAge.element(idx).assign(makeVec4(nextVelocity, age));
          renderState.element(idx).assign(makeVec4(
            base.x.mul(sizeScale),
            base.y.mul(makeFloat(1).sub(t)),
            fadeCore ? base.z.mul(makeFloat(1).sub(t)) : base.z,
            t,
          ));
        });
      });
    })().compute(this.maxCount, [64]);
  }
}

function makeStorageAttribute(array: Float32Array): StorageInstancedBufferAttribute {
  const attribute = new StorageInstancedBufferAttribute(array, 4);
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}

function markStorageRange(attribute: StorageInstancedBufferAttribute, start: number, count: number): void {
  attribute.addUpdateRange(start, count);
  attribute.needsUpdate = true;
}

/** Dust trail behind racer when on dirt / off-track. Cheap point sprites. */
export class DustTrail {
  readonly points: THREE.Points | THREE.InstancedMesh;
  private readonly geom: THREE.BufferGeometry;
  private readonly positions: Float32Array;
  private readonly sizes: Float32Array;
  private readonly alphas: Float32Array;
  private readonly ages: Float32Array;
  private readonly randoms: Float32Array;
  readonly config: DustTrailConfig;
  private readonly particles: Particle[] = [];
  private readonly gpuBillboards: boolean;
  private readonly gpuUpdater: GpuParticleUpdater | null = null;
  private readonly rendererMode: RendererMode;
  private spawnAccumulator = 0;

  constructor(
    config?: PartialConfig<DustTrailConfig>,
    rendererMode: RendererMode = 'webgl2',
    runtime?: WebGPUParticleRuntime,
  ) {
    this.rendererMode = rendererMode;
    this.config = mergeParticleConfig(DEFAULT_PARTICLES_CONFIG.dustTrail, config);
    this.geom = new THREE.BufferGeometry();
    this.positions = new Float32Array(MAX_PARTICLES * 3);
    this.sizes = new Float32Array(MAX_PARTICLES);
    this.alphas = new Float32Array(MAX_PARTICLES);
    this.ages = new Float32Array(MAX_PARTICLES);
    this.randoms = new Float32Array(MAX_PARTICLES);
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geom.setAttribute('aSize', new THREE.BufferAttribute(this.sizes, 1));
    this.geom.setAttribute('aAlpha', new THREE.BufferAttribute(this.alphas, 1));
    this.geom.setDrawRange(0, 0);
    this.gpuBillboards = rendererMode === 'webgpu' && gpuParticleBillboardsEnabled();
    if (rendererMode === 'webgpu' && gpuDustComputeEnabled(runtime)) {
      this.gpuUpdater = new GpuParticleUpdater(
        MAX_PARTICLES,
        runtime.renderer,
        runtime.gpuTimer ?? null,
        'dust',
        {
          verticalAccel: -this.config.gravity,
          drag: this.config.drag,
          sizeGrowth: this.config.sizeGrowth,
          shrinkStart: this.config.webgpuDissolveStart,
          shrinkEndScale: this.config.webgpuEndScale,
          fadeCore: false,
        },
      );
    }

    const mat = rendererMode === 'webgpu'
      ? null
      : new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        vertexShader: `
          attribute float aSize;
          attribute float aAlpha;
          varying float vAlpha;
          void main() {
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_Position = projectionMatrix * mv;
            gl_PointSize = aSize * (300.0 / -mv.z);
            vAlpha = aAlpha;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          void main() {
            vec2 uv = gl_PointCoord - 0.5;
            float d = length(uv);
            float a = smoothstep(0.5, 0.2, d) * vAlpha;
            gl_FragColor = vec4(0.85, 0.62, 0.36, a);
          }
        `,
      });

    if (rendererMode === 'webgpu') {
      const gpuCompute = this.gpuUpdater !== null;
      const geometry = gpuCompute
        ? makeComputeBillboardGeometry(this.gpuUpdater!)
        : makeBillboardGeometry(MAX_PARTICLES, this.positions, this.sizes, this.alphas, undefined, this.ages, this.randoms);
      const material = makeWebGPUDustMaterial(this.gpuBillboards, gpuCompute, this.config);
      const mesh = new THREE.InstancedMesh(geometry, material, MAX_PARTICLES);
      mesh.count = 0;
      mesh.frustumCulled = false;
      if (this.gpuBillboards) installBillboardBasisSync(mesh, material);
      else installBillboardSync(mesh, this.positions, this.sizes);
      this.points = mesh;
    } else {
      this.points = new THREE.Points(this.geom, mat!);
      this.points.frustumCulled = false;
    }
  }

  /** speed is current racer speed (m/s); offTrack increases spawn rate. */
  spawn(
    racerPos: THREE.Vector3,
    racerHeading: number,
    speed: number,
    offTrack: boolean,
    dt: number,
  ): void {
    const config = this.config;
    const baseRate = offTrack ? config.offTrackSpawnRate : config.onTrackSpawnRate;
    const rate = baseRate * THREE.MathUtils.clamp(speed / Math.max(0.0001, config.speedReference), 0, 1);
    this.spawnAccumulator += rate * dt;
    while (this.spawnAccumulator >= 1 && (this.gpuUpdater ? this.gpuUpdater.activeCount < MAX_PARTICLES : this.particles.length < MAX_PARTICLES)) {
      this.spawnAccumulator -= 1;
      const fwdX = Math.sin(racerHeading);
      const fwdZ = Math.cos(racerHeading);
      const offset = -config.rearOffset;
      const jitterX = (Math.random() - 0.5) * config.spread;
      const jitterZ = (Math.random() - 0.5) * config.spread;
      const particle = {
        age: 0,
        life: offTrack ? config.offTrackLifetimeSec : config.onTrackLifetimeSec,
        x: racerPos.x + fwdX * offset + jitterX,
        y: racerPos.y + config.yOffset,
        z: racerPos.z + fwdZ * offset + jitterZ,
        vx: -fwdX * (config.backwardSpeedMin + Math.random() * config.backwardSpeedJitter)
          + (Math.random() - 0.5) * config.lateralVelocityJitter,
        vy: config.upwardVelocityMin + Math.random() * config.upwardVelocityJitter,
        vz: -fwdZ * (config.backwardSpeedMin + Math.random() * config.backwardSpeedJitter)
          + (Math.random() - 0.5) * config.lateralVelocityJitter,
        size: offTrack
          ? config.offTrackSizeMin + Math.random() * (config.offTrackSizeMax - config.offTrackSizeMin)
          : config.onTrackSizeMin + Math.random() * (config.onTrackSizeMax - config.onTrackSizeMin),
        alpha: offTrack ? config.offTrackOpacity : config.onTrackOpacity,
        random: Math.random(),
      };
      if (this.gpuUpdater) {
        this.gpuUpdater.spawn({ ...particle, core: particle.random });
      } else {
        this.particles.push(particle);
      }
    }
  }

  update(dt: number): void {
    this.syncWebGPUUniforms();
    if (this.gpuUpdater) {
      this.gpuUpdater.update(dt);
      if (this.points instanceof THREE.InstancedMesh) this.points.count = this.gpuUpdater.drawCount;
      return;
    }

    const config = this.config;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;
      if (p.age >= p.life) {
        const last = this.particles.length - 1;
        if (i !== last) this.particles[i] = this.particles[last];
        this.particles.pop();
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy -= config.gravity * dt;
      const damping = 1 - config.drag * dt;
      p.vx *= damping;
      p.vz *= damping;
    }

    const count = this.particles.length;
    for (let i = 0; i < count; i++) {
      const p = this.particles[i];
      const t = p.age / p.life;
      this.positions[i * 3 + 0] = p.x;
      this.positions[i * 3 + 1] = p.y;
      this.positions[i * 3 + 2] = p.z;
      const shrinkT = this.rendererMode === 'webgpu'
        ? THREE.MathUtils.clamp((t - config.webgpuDissolveStart) / Math.max(0.0001, 1 - config.webgpuDissolveStart), 0, 1)
        : 0;
      const sizeScale = (1 + t * config.sizeGrowth) * (1 + shrinkT * ((this.rendererMode === 'webgpu' ? config.webgpuEndScale : 1) - 1));
      this.sizes[i] = p.size * sizeScale;
      this.alphas[i] = p.alpha * (1 - t);
      this.ages[i] = t;
      this.randoms[i] = p.random ?? 0;
    }

    if (this.points instanceof THREE.InstancedMesh) {
      this.points.count = count;
      (this.points.geometry.getAttribute('iAlpha') as THREE.InstancedBufferAttribute).needsUpdate = true;
      const ageAttribute = this.points.geometry.getAttribute('iAge') as THREE.InstancedBufferAttribute | undefined;
      if (ageAttribute) ageAttribute.needsUpdate = true;
      const randomAttribute = this.points.geometry.getAttribute('iRandom') as THREE.InstancedBufferAttribute | undefined;
      if (randomAttribute) randomAttribute.needsUpdate = true;
      if (this.gpuBillboards) {
        (this.points.geometry.getAttribute('iPosition') as THREE.InstancedBufferAttribute).needsUpdate = true;
        (this.points.geometry.getAttribute('iSize') as THREE.InstancedBufferAttribute).needsUpdate = true;
      }
    } else {
      (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.getAttribute('aSize') as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.getAttribute('aAlpha') as THREE.BufferAttribute).needsUpdate = true;
      this.geom.setDrawRange(0, count);
    }
  }

  clear(): void {
    if (this.gpuUpdater) {
      this.gpuUpdater.clear();
      if (this.points instanceof THREE.InstancedMesh) this.points.count = 0;
      return;
    }
    this.particles.length = 0;
    this.spawnAccumulator = 0;
    this.geom.setDrawRange(0, 0);
    if (this.points instanceof THREE.InstancedMesh) this.points.count = 0;
  }

  debugParticleCount(): number {
    if (this.gpuUpdater) return this.gpuUpdater.activeCount;
    return this.particles.length;
  }

  private syncWebGPUUniforms(): void {
    if (!(this.points instanceof THREE.InstancedMesh)) return;
    const material = this.points.material as WebGPUBillboardMaterial;
    const uniforms = material.userData.webgpuDustUniforms;
    if (!uniforms) return;
    uniforms.edgeSoftness.value = this.config.webgpuEdgeSoftness;
    uniforms.dissolveStrength.value = this.config.webgpuDissolveStrength;
    uniforms.dissolveStart.value = this.config.webgpuDissolveStart;
    uniforms.noiseScale.value = this.config.webgpuNoiseScale;
    uniforms.erosionFeather.value = this.config.webgpuErosionFeather;
    uniforms.puffContrast.value = this.config.webgpuPuffContrast;
    uniforms.puffStretch.value = this.config.webgpuPuffStretch;
    uniforms.fadeStart.value = this.config.webgpuFadeStart;
    uniforms.fadePower.value = this.config.webgpuFadePower;
  }
}

/** Additive blue engine glow and restrained exhaust. No texture asset needed. */
export class EngineTrail {
  readonly group = new THREE.Group();
  readonly points: THREE.Points | THREE.InstancedMesh;
  private readonly geom: THREE.BufferGeometry;
  private readonly positions: Float32Array;
  private readonly sizes: Float32Array;
  private readonly alphas: Float32Array;
  private readonly cores: Float32Array;
  readonly config: EngineTrailConfig;
  private readonly particles: EngineParticle[] = [];
  private readonly gpuBillboards: boolean;
  private readonly gpuUpdater: GpuParticleUpdater | null = null;
  private readonly rendererMode: RendererMode;
  private readonly plumeMeshes: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>[] = [];
  private spawnAccumulator = 0;

  constructor(
    config?: PartialConfig<EngineTrailConfig>,
    rendererMode: RendererMode = 'webgl2',
    runtime?: WebGPUParticleRuntime,
  ) {
    this.rendererMode = rendererMode;
    this.config = mergeParticleConfig(DEFAULT_PARTICLES_CONFIG.engineTrail, config);
    this.geom = new THREE.BufferGeometry();
    this.positions = new Float32Array(MAX_ENGINE_PARTICLES * 3);
    this.sizes = new Float32Array(MAX_ENGINE_PARTICLES);
    this.alphas = new Float32Array(MAX_ENGINE_PARTICLES);
    this.cores = new Float32Array(MAX_ENGINE_PARTICLES);
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geom.setAttribute('aSize', new THREE.BufferAttribute(this.sizes, 1));
    this.geom.setAttribute('aAlpha', new THREE.BufferAttribute(this.alphas, 1));
    this.geom.setAttribute('aCore', new THREE.BufferAttribute(this.cores, 1));
    this.geom.setDrawRange(0, 0);
    this.gpuBillboards = rendererMode === 'webgpu' && gpuParticleBillboardsEnabled();
    if (rendererMode === 'webgpu' && gpuParticleComputeEnabled(runtime)) {
      this.gpuUpdater = new GpuParticleUpdater(
        MAX_ENGINE_PARTICLES,
        runtime.renderer,
        runtime.gpuTimer ?? null,
        'engine',
        {
          verticalAccel: this.config.upwardDrift,
          drag: this.config.drag,
          sizeGrowth: this.config.sizeGrowth,
        },
      );
    }

    const mat = rendererMode === 'webgpu'
      ? null
      : new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexShader: `
          attribute float aSize;
          attribute float aAlpha;
          attribute float aCore;
          varying float vAlpha;
          varying float vCore;
          void main() {
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_Position = projectionMatrix * mv;
            gl_PointSize = aSize * (260.0 / -mv.z);
            vAlpha = aAlpha;
            vCore = aCore;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          varying float vCore;
          void main() {
            vec2 uv = gl_PointCoord - 0.5;
            float d = length(uv);
            float coreMask = smoothstep(0.18, 0.02, d);
            float glow = smoothstep(0.5, 0.08, d);
            vec3 outer = vec3(0.05, 0.34, 1.0);
            vec3 inner = vec3(0.65, 0.95, 1.0);
            vec3 color = mix(outer, inner, max(coreMask, vCore));
            float alpha = (glow * 0.42 + coreMask * 0.9) * vAlpha;
            // Match EngineCore: keep per-particle source below the global bloom
            // threshold (~1.5 in linear HDR). Additive blending still stacks the
            // glow visually; the post-FX bloom pass just stops grabbing it.
            color = min(color, vec3(1.4));
            gl_FragColor = vec4(color, alpha);
          }
        `,
      });

    if (rendererMode === 'webgpu') {
      const gpuCompute = this.gpuUpdater !== null;
      const geometry = gpuCompute
        ? makeComputeBillboardGeometry(this.gpuUpdater!)
        : makeBillboardGeometry(MAX_ENGINE_PARTICLES, this.positions, this.sizes, this.alphas, this.cores);
      const material = makeWebGPUEngineTrailMaterial(this.gpuBillboards, gpuCompute);
      const mesh = new THREE.InstancedMesh(geometry, material, MAX_ENGINE_PARTICLES);
      mesh.count = 0;
      mesh.frustumCulled = false;
      if (this.gpuBillboards) installBillboardBasisSync(mesh, material);
      else installBillboardSync(mesh, this.positions, this.sizes, ENGINE_POINT_SIZE_REFERENCE_PX);
      this.points = mesh;
      this.installWebGPUPlumes(MAX_ENGINE_PARTICLES);
    } else {
      this.points = new THREE.Points(this.geom, mat!);
      this.points.frustumCulled = false;
    }
    this.group.add(this.points);
  }

  spawn(
    nozzlePositions: readonly THREE.Vector3[],
    racerHeading: number,
    speed: number,
    nozzleIntensities: readonly number[],
    dt: number,
  ): void {
    if (nozzlePositions.length === 0) return;
    const config = this.config;
    // Allow intensities up to 2.0 so an active boost can amplify the
    // plume beyond normal full-throttle. Particle size, alpha, ejection
    // speed, and spawn rate all scale linearly past 1.0 — so a boost
    // value of 1.5 reads as a visible afterburner stage.
    let maxIntensity = 0;
    for (const v of nozzleIntensities) {
      const c = THREE.MathUtils.clamp(v, 0, config.maxIntensity);
      if (c > maxIntensity) maxIntensity = c;
    }
    this.updateWebGPUPlumes(nozzlePositions, racerHeading, nozzleIntensities, maxIntensity);
    if (maxIntensity <= config.idleThreshold) return;

    this.spawnNozzleGlow(nozzlePositions, nozzleIntensities);
    // Spawn rate scales with the brighter side so a single-engine roar
    // still produces a steady plume; particles per nozzle then scale with
    // that side's intensity below.
    this.spawnAccumulator += (config.spawnRateBase + config.spawnRateIntensity * maxIntensity) * dt;
    while (this.spawnAccumulator >= 1 && (this.gpuUpdater ? this.gpuUpdater.activeCount < MAX_ENGINE_PARTICLES : this.particles.length < MAX_ENGINE_PARTICLES)) {
      this.spawnAccumulator -= 1;
      for (let i = 0; i < nozzlePositions.length; i++) {
        if ((this.gpuUpdater?.activeCount ?? this.particles.length) >= MAX_ENGINE_PARTICLES) break;
        const nozzleI = THREE.MathUtils.clamp(nozzleIntensities[i] ?? 0, 0, config.maxIntensity);
        if (nozzleI <= config.idleThreshold) continue;
        this.spawnOne(nozzlePositions[i], racerHeading, speed, nozzleI);
      }
    }
  }

  update(dt: number): void {
    if (this.gpuUpdater) {
      this.gpuUpdater.update(dt);
      if (this.points instanceof THREE.InstancedMesh) this.points.count = this.gpuUpdater.drawCount;
      return;
    }

    const config = this.config;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;
      if (p.age >= p.life) {
        const last = this.particles.length - 1;
        if (i !== last) this.particles[i] = this.particles[last];
        this.particles.pop();
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy += config.upwardDrift * dt;
      const damping = 1 - config.drag * dt;
      p.vx *= damping;
      p.vz *= damping;
    }

    const count = this.particles.length;
    for (let i = 0; i < count; i++) {
      const p = this.particles[i];
      const t = p.age / p.life;
      this.positions[i * 3 + 0] = p.x;
      this.positions[i * 3 + 1] = p.y;
      this.positions[i * 3 + 2] = p.z;
      this.sizes[i] = p.size * (1 + t * config.sizeGrowth);
      this.alphas[i] = p.alpha * (1 - t);
      this.cores[i] = p.core * (1 - t);
    }

    if (this.points instanceof THREE.InstancedMesh) {
      this.points.count = count;
      (this.points.geometry.getAttribute('iAlpha') as THREE.InstancedBufferAttribute).needsUpdate = true;
      (this.points.geometry.getAttribute('iCore') as THREE.InstancedBufferAttribute).needsUpdate = true;
      if (this.gpuBillboards) {
        (this.points.geometry.getAttribute('iPosition') as THREE.InstancedBufferAttribute).needsUpdate = true;
        (this.points.geometry.getAttribute('iSize') as THREE.InstancedBufferAttribute).needsUpdate = true;
      }
    } else {
      (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.getAttribute('aSize') as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.getAttribute('aAlpha') as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.getAttribute('aCore') as THREE.BufferAttribute).needsUpdate = true;
      this.geom.setDrawRange(0, count);
    }
  }

  debugParticleCount(): number {
    if (this.gpuUpdater) return this.gpuUpdater.activeCount;
    return this.particles.length;
  }

  clear(): void {
    if (this.gpuUpdater) {
      this.gpuUpdater.clear();
      if (this.points instanceof THREE.InstancedMesh) this.points.count = 0;
      this.hideWebGPUPlumes();
      return;
    }
    this.particles.length = 0;
    this.spawnAccumulator = 0;
    this.geom.setDrawRange(0, 0);
    if (this.points instanceof THREE.InstancedMesh) this.points.count = 0;
    this.hideWebGPUPlumes();
  }

  private installWebGPUPlumes(maxCount: number): void {
    if (this.rendererMode !== 'webgpu' || !WEBGPU_ENGINE_PLUME_CONES_ENABLED) return;
    const count = Math.min(2, maxCount);
    for (let i = 0; i < count; i++) {
      const geometry = new THREE.CylinderGeometry(1, 0.24, 1, 20, 1, true);
      const material = makeWebGPUPlumeMaterial();
      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = `webgpu-engine-plume-${i}`;
      mesh.frustumCulled = false;
      mesh.visible = false;
      this.plumeMeshes.push(mesh);
      this.group.add(mesh);
    }
  }

  private hideWebGPUPlumes(): void {
    for (const mesh of this.plumeMeshes) {
      mesh.visible = false;
      mesh.material.opacity = 0;
    }
  }

  private updateWebGPUPlumes(
    nozzlePositions: readonly THREE.Vector3[],
    racerHeading: number,
    nozzleIntensities: readonly number[],
    maxIntensity: number,
  ): void {
    if (
      this.rendererMode !== 'webgpu'
      || !WEBGPU_ENGINE_PLUME_CONES_ENABLED
      || !this.config.webgpuPlumeEnabled
      || maxIntensity <= this.config.idleThreshold
    ) {
      this.hideWebGPUPlumes();
      return;
    }
    const fwdX = Math.sin(racerHeading);
    const fwdZ = Math.cos(racerHeading);
    plumeDirection.set(-fwdX, -0.04, -fwdZ).normalize();
    plumeQuaternion.setFromUnitVectors(plumeYAxis, plumeDirection);
    const time = typeof performance !== 'undefined' ? performance.now() * 0.001 : Date.now() * 0.001;
    for (let i = 0; i < this.plumeMeshes.length; i++) {
      const mesh = this.plumeMeshes[i];
      const nozzle = nozzlePositions[i];
      const intensity = THREE.MathUtils.clamp(nozzleIntensities[i] ?? 0, 0, this.config.maxIntensity);
      if (!nozzle || intensity <= this.config.idleThreshold) {
        mesh.visible = false;
        mesh.material.opacity = 0;
        continue;
      }
      const flicker = 1 + Math.sin(time * 42 + i * 2.3) * this.config.webgpuPlumeFlicker;
      const length = Math.max(0.01, (this.config.webgpuPlumeLengthBase + intensity * this.config.webgpuPlumeLengthIntensity) * flicker);
      const radius = Math.max(0.01, this.config.webgpuPlumeRadiusBase + intensity * this.config.webgpuPlumeRadiusIntensity);
      plumePosition.copy(nozzle).addScaledVector(plumeDirection, length * 0.5);
      mesh.visible = true;
      mesh.position.copy(plumePosition);
      mesh.quaternion.copy(plumeQuaternion);
      mesh.scale.set(radius, length, radius);
      mesh.material.opacity = this.config.webgpuPlumeOpacity * THREE.MathUtils.clamp(intensity / Math.max(0.001, this.config.maxIntensity), 0, 1);
    }
  }

  private spawnOne(
    nozzle: THREE.Vector3,
    racerHeading: number,
    speed: number,
    intensity: number,
  ): void {
    const config = this.config;
    const fwdX = Math.sin(racerHeading);
    const fwdZ = Math.cos(racerHeading);
    const sideX = Math.cos(racerHeading);
    const sideZ = -Math.sin(racerHeading);
    const sideJitter = (Math.random() - 0.5) * (config.sideJitterBase + intensity * config.sideJitterIntensity);
    const verticalJitter = (Math.random() - 0.5) * (config.verticalJitterBase + intensity * config.verticalJitterIntensity);
    const rearSpeed = config.rearSpeedBase
      + speed * config.rearSpeedByWorldSpeed
      + intensity * config.rearSpeedByIntensity
      + Math.random() * config.rearSpeedJitter;
    const particle = {
      age: 0,
      life: config.lifetimeSec + Math.random() * config.lifetimeJitterSec,
      x: nozzle.x + sideX * sideJitter - fwdX * (config.spawnBackOffsetBase + intensity * config.spawnBackOffsetIntensity),
      y: nozzle.y + verticalJitter,
      z: nozzle.z + sideZ * sideJitter - fwdZ * (config.spawnBackOffsetBase + intensity * config.spawnBackOffsetIntensity),
      vx: -fwdX * rearSpeed + sideX * (Math.random() - 0.5) * config.sideVelocityJitter,
      vy: (Math.random() + config.verticalVelocityBase) * config.verticalVelocityJitter,
      vz: -fwdZ * rearSpeed + sideZ * (Math.random() - 0.5) * config.sideVelocityJitter,
      size: config.sizeBase + intensity * config.sizeIntensity + Math.random() * config.sizeJitter,
      alpha: config.opacityBase + intensity * config.opacityIntensity,
      core: config.coreMin + Math.random() * config.coreJitter,
    };
    if (this.gpuUpdater) this.gpuUpdater.spawn(particle);
    else this.particles.push(particle);
  }

  private spawnNozzleGlow(
    nozzlePositions: readonly THREE.Vector3[],
    nozzleIntensities: readonly number[],
  ): void {
    const config = this.config;
    if (!config.nozzleGlowEnabled) return;
    if ((this.gpuUpdater?.activeCount ?? this.particles.length) >= MAX_ENGINE_PARTICLES - nozzlePositions.length) return;
    for (let i = 0; i < nozzlePositions.length; i++) {
      const nozzle = nozzlePositions[i];
      const intensity = THREE.MathUtils.clamp(nozzleIntensities[i] ?? 0, 0, config.maxIntensity);
      if (intensity <= config.idleThreshold) continue;
      const particle = {
        age: 0,
        life: config.nozzleGlowLifeSec,
        x: nozzle.x,
        y: nozzle.y,
        z: nozzle.z,
        vx: 0,
        vy: 0,
        vz: 0,
        size: config.nozzleGlowSizeBase + intensity * config.nozzleGlowSizeIntensity,
        alpha: config.nozzleGlowOpacityBase + intensity * config.nozzleGlowOpacityIntensity,
        core: 1,
      };
      if (this.gpuUpdater) this.gpuUpdater.spawn(particle);
      else this.particles.push(particle);
    }
  }
}

/** Long-lived scrape flecks from hard banking near the track surface. */
export class ScrapeMarks {
  readonly group = new THREE.Group();
  readonly marks: THREE.Mesh;
  readonly sparks: THREE.LineSegments;
  readonly points: THREE.LineSegments;
  private readonly config: ScrapeMarksConfig;
  private readonly markGeom: THREE.BufferGeometry;
  private readonly markPositions: Float32Array;
  private readonly markAlphas: Float32Array;
  private readonly markIndices: number[] = [];
  private readonly sparkGeom: THREE.BufferGeometry;
  private readonly sparkPositions: Float32Array;
  private readonly sparkAlphas: Float32Array;
  private readonly sparkHeats: Float32Array;
  private readonly marksData: ScrapeMark[] = [];
  private readonly sparkData: SparkParticle[] = [];
  private markAccumulator = 0;
  private sparkAccumulator = 0;

  constructor(config?: PartialConfig<ScrapeMarksConfig>, rendererMode: RendererMode = 'webgl2') {
    this.config = mergeParticleConfig(DEFAULT_PARTICLES_CONFIG.scrapeMarks, config);
    this.markGeom = new THREE.BufferGeometry();
    this.markPositions = new Float32Array(MAX_SCRAPE_MARKS * 4 * 3);
    this.markAlphas = new Float32Array(MAX_SCRAPE_MARKS * 4);
    for (let i = 0; i < MAX_SCRAPE_MARKS; i++) {
      const base = i * 4;
      this.markIndices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    this.markGeom.setAttribute('position', new THREE.BufferAttribute(this.markPositions, 3));
    this.markGeom.setAttribute('aAlpha', new THREE.BufferAttribute(this.markAlphas, 1));
    this.markGeom.setIndex(this.markIndices);
    this.markGeom.setDrawRange(0, 0);

    const markMat = rendererMode === 'webgpu'
      ? makeWebGPUScrapeMarkMaterial()
      : new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        vertexShader: `
          attribute float aAlpha;
          varying float vAlpha;
          void main() {
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            vAlpha = aAlpha;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          void main() {
            gl_FragColor = vec4(0.06, 0.045, 0.035, vAlpha);
          }
        `,
      });
    this.marks = new THREE.Mesh(this.markGeom, markMat);
    this.marks.frustumCulled = false;
    this.marks.renderOrder = 4;

    this.sparkGeom = new THREE.BufferGeometry();
    this.sparkPositions = new Float32Array(MAX_SCRAPE_SPARKS * 2 * 3);
    this.sparkAlphas = new Float32Array(MAX_SCRAPE_SPARKS * 2);
    this.sparkHeats = new Float32Array(MAX_SCRAPE_SPARKS * 2);
    this.sparkGeom.setAttribute('position', new THREE.BufferAttribute(this.sparkPositions, 3));
    this.sparkGeom.setAttribute('aAlpha', new THREE.BufferAttribute(this.sparkAlphas, 1));
    this.sparkGeom.setAttribute('aHeat', new THREE.BufferAttribute(this.sparkHeats, 1));
    this.sparkGeom.setDrawRange(0, 0);

    const sparkMat = rendererMode === 'webgpu'
      ? makeWebGPUScrapeSparkMaterial()
      : new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexShader: `
          attribute float aAlpha;
          attribute float aHeat;
          varying float vAlpha;
          varying float vHeat;
          void main() {
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_Position = projectionMatrix * mv;
            vAlpha = aAlpha;
            vHeat = aHeat;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          varying float vHeat;
          void main() {
            vec3 ember = vec3(1.0, 0.34, 0.04);
            vec3 hot = vec3(1.0, 0.88, 0.28);
            vec3 color = mix(ember, hot, vHeat);
            gl_FragColor = vec4(color, vAlpha);
          }
        `,
      });

    this.sparks = new THREE.LineSegments(this.sparkGeom, sparkMat);
    this.sparks.frustumCulled = false;
    this.sparks.renderOrder = 7;
    this.points = this.sparks;
    this.group.add(this.marks, this.sparks);
  }

  /**
   * One-shot spark fan triggered by a discrete wall impact. Sparks fly along
   * the contact tangent (so they trail the direction the racer was moving
   * along the wall), with a small outward bias along the wall normal so
   * they're visible against the surface rather than burrowing into it.
   *
   * `tangentX/Z` and `normalX/Z` are unit XZ-plane vectors. `normalX/Z`
   * points OUT of the wall, toward the racer side. `intensity` is 0..1 —
   * 0 spawns nothing, 1 spawns a full burst (~20 sparks).
   */
  impactBurst(
    x: number,
    y: number,
    z: number,
    tangentX: number,
    tangentZ: number,
    normalX: number,
    normalZ: number,
    intensity: number,
  ): void {
    if (intensity <= 0) return;
    const config = this.config;
    const cap = THREE.MathUtils.clamp(intensity, 0, 1);
    const count = 6 + Math.round(cap * 14);
    for (let i = 0; i < count; i++) {
      if (this.sparkData.length >= MAX_SCRAPE_SPARKS) this.sparkData.shift();
      // Bidirectional tangent fan with a small outward normal bias so
      // sparks read above the surface rather than into it.
      const tangentSign = i % 2 === 0 ? 1 : -1;
      const tangentSpeed = (4 + cap * 18) * (0.5 + Math.random());
      const normalSpeed = (1.5 + cap * 4) * Math.random();
      const dirX = tangentX * tangentSign + normalX * 0.45;
      const dirZ = tangentZ * tangentSign + normalZ * 0.45;
      const dirLen = Math.hypot(dirX, dirZ) || 1;
      const vx = (dirX / dirLen) * tangentSpeed + normalX * normalSpeed;
      const vz = (dirZ / dirLen) * tangentSpeed + normalZ * normalSpeed;
      this.sparkData.push({
        age: 0,
        life: config.sparkLifeMin + Math.random() * (config.sparkLifeJitter + 0.25),
        x: x + (Math.random() - 0.5) * 0.2,
        y: y + config.sparkYBase + Math.random() * config.sparkYJitter,
        z: z + (Math.random() - 0.5) * 0.2,
        vx,
        vy: 1.2 + Math.random() * 2.8 + cap * 1.8,
        vz,
        size: config.sparkSizeBase + cap * config.sparkSizeIntensity * 1.4 + Math.random() * config.sparkSizeJitter,
        alpha: Math.min(1, config.sparkOpacityBase + cap * config.sparkOpacityIntensity * 1.5),
        heat: Math.min(1, config.sparkHeatBase + 0.2 + Math.random() * config.sparkHeatJitter),
      });
    }
  }

  /**
   * Continuous low-rate spark trickle at a wall contact point. Rate scales
   * with `intensity` (the smoothed contact-intensity signal). One emission
   * yields 1–2 sparks; over time this paints the contact location with the
   * "we are still on the wall" feel. Cheap — uses the same particle pool
   * as impactBurst.
   */
  spawnContactTrickle(
    x: number,
    y: number,
    z: number,
    tangentX: number,
    tangentZ: number,
    normalX: number,
    normalZ: number,
    intensity: number,
    dt: number,
  ): void {
    if (intensity <= 0.05 || dt <= 0) return;
    const config = this.config;
    const cap = THREE.MathUtils.clamp(intensity, 0, 1);
    const rate = 4 + cap * 28; // sparks per second
    this.sparkAccumulator += rate * dt;
    while (this.sparkAccumulator >= 1) {
      this.sparkAccumulator -= 1;
      if (this.sparkData.length >= MAX_SCRAPE_SPARKS) this.sparkData.shift();
      const tangentSign = Math.random() < 0.5 ? 1 : -1;
      const tangentSpeed = (3 + cap * 10) * (0.6 + Math.random() * 0.7);
      const normalSpeed = 1 + cap * 2 + Math.random() * 1.2;
      this.sparkData.push({
        age: 0,
        life: config.sparkLifeMin + Math.random() * config.sparkLifeJitter,
        x: x + (Math.random() - 0.5) * 0.18,
        y: y + config.sparkYBase + Math.random() * config.sparkYJitter,
        z: z + (Math.random() - 0.5) * 0.18,
        vx: tangentX * tangentSign * tangentSpeed + normalX * normalSpeed,
        vy: 0.8 + Math.random() * 1.4 + cap * 1.0,
        vz: tangentZ * tangentSign * tangentSpeed + normalZ * normalSpeed,
        size: config.sparkSizeBase + cap * config.sparkSizeIntensity * 0.7 + Math.random() * config.sparkSizeJitter,
        alpha: Math.min(1, config.sparkOpacityBase + cap * config.sparkOpacityIntensity),
        heat: Math.min(1, config.sparkHeatBase + Math.random() * config.sparkHeatJitter),
      });
    }
  }

  spawn(
    emitters: readonly ScrapeEmitter[],
    racerHeading: number,
    speed: number,
    dt: number,
  ): void {
    if (emitters.length === 0) return;
    const config = this.config;
    const speedSignal = THREE.MathUtils.clamp(speed / Math.max(0.0001, config.speedReference), 0, 1);
    const markRate = config.markSpawnRate * speedSignal;
    const sparkRate = config.sparkSpawnRate * speedSignal;
    for (const emitter of emitters) {
      const intensity = THREE.MathUtils.clamp(emitter.intensity, 0, 1);
      this.markAccumulator += markRate * intensity * dt;
      while (this.markAccumulator >= 1) {
        this.markAccumulator -= 1;
        this.spawnMark(emitter.position, racerHeading, intensity);
      }
      this.sparkAccumulator += sparkRate * intensity * dt;
      while (this.sparkAccumulator >= 1) {
        this.sparkAccumulator -= 1;
        this.spawnSpark(emitter.position, racerHeading, speed, intensity);
      }
    }
  }

  update(dt: number): void {
    const config = this.config;
    for (let i = this.marksData.length - 1; i >= 0; i--) {
      const mark = this.marksData[i];
      mark.age += dt;
      if (mark.age >= mark.life) {
        const last = this.marksData.length - 1;
        if (i !== last) this.marksData[i] = this.marksData[last];
        this.marksData.pop();
      }
    }

    for (let i = this.sparkData.length - 1; i >= 0; i--) {
      const p = this.sparkData[i];
      p.age += dt;
      if (p.age >= p.life) {
        const last = this.sparkData.length - 1;
        if (i !== last) this.sparkData[i] = this.sparkData[last];
        this.sparkData.pop();
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      const damping = Math.max(0, 1 - config.sparkDrag * dt);
      p.vx *= damping;
      p.vz *= damping;
      p.vy -= config.sparkGravity * dt;
      p.heat *= Math.max(0, 1 - config.sparkCooling * dt);
    }

    this.updateMarkGeometry();
    this.updateSparkGeometry();
  }

  debugParticleCount(): number {
    return this.marksData.length + this.sparkData.length;
  }

  debugMarkCount(): number {
    return this.marksData.length;
  }

  debugSparkCount(): number {
    return this.sparkData.length;
  }

  clear(): void {
    this.marksData.length = 0;
    this.sparkData.length = 0;
    this.markAccumulator = 0;
    this.sparkAccumulator = 0;
    this.markGeom.setDrawRange(0, 0);
    this.sparkGeom.setDrawRange(0, 0);
  }

  private spawnMark(
    position: THREE.Vector3,
    racerHeading: number,
    intensity: number,
  ): void {
    const config = this.config;
    if (this.marksData.length >= MAX_SCRAPE_MARKS) this.marksData.shift();
    this.marksData.push({
      age: 0,
      life: config.markLifeMin + Math.random() * config.markLifeJitter,
      x: position.x + (Math.random() - 0.5) * config.markPositionJitter,
      y: position.y + config.markYOffset,
      z: position.z + (Math.random() - 0.5) * config.markPositionJitter,
      heading: racerHeading + (Math.random() - 0.5) * config.markHeadingJitter,
      length: config.markLengthBase + intensity * config.markLengthIntensity + Math.random() * config.markLengthJitter,
      width: config.markWidthBase + intensity * config.markWidthIntensity + Math.random() * config.markWidthJitter,
      alpha: config.markOpacityBase + intensity * config.markOpacityIntensity,
    });
  }

  private spawnSpark(
    position: THREE.Vector3,
    racerHeading: number,
    speed: number,
    intensity: number,
  ): void {
    const config = this.config;
    if (this.sparkData.length >= MAX_SCRAPE_SPARKS) this.sparkData.shift();
    const fwdX = Math.sin(racerHeading);
    const fwdZ = Math.cos(racerHeading);
    const sideX = Math.cos(racerHeading);
    const sideZ = -Math.sin(racerHeading);
    const lateral = (Math.random() - 0.5) * config.sparkLateralJitter;
    const rear = Math.random() * config.sparkRearJitter;
    const scatter = config.sparkScatterBase + intensity * config.sparkScatterIntensity + speed * config.sparkScatterSpeed;
    this.sparkData.push({
      age: 0,
      life: config.sparkLifeMin + Math.random() * config.sparkLifeJitter,
      x: position.x + sideX * lateral - fwdX * rear,
      y: position.y + config.sparkYBase + Math.random() * config.sparkYJitter,
      z: position.z + sideZ * lateral - fwdZ * rear,
      vx: -fwdX * scatter * (config.sparkRearVelocityMin + Math.random() * config.sparkRearVelocityJitter)
        + sideX * (Math.random() - 0.5) * scatter * config.sparkSideVelocityJitter,
      vy: config.sparkUpVelocityBase + Math.random() * config.sparkUpVelocityJitter + intensity * config.sparkUpVelocityIntensity,
      vz: -fwdZ * scatter * (config.sparkRearVelocityMin + Math.random() * config.sparkRearVelocityJitter)
        + sideZ * (Math.random() - 0.5) * scatter * config.sparkSideVelocityJitter,
      size: config.sparkSizeBase + intensity * config.sparkSizeIntensity + Math.random() * config.sparkSizeJitter,
      alpha: config.sparkOpacityBase + intensity * config.sparkOpacityIntensity,
      heat: config.sparkHeatBase + Math.random() * config.sparkHeatJitter,
    });
  }

  private updateMarkGeometry(): void {
    const config = this.config;
    const count = this.marksData.length;
    for (let i = 0; i < count; i++) {
      const mark = this.marksData[i];
      const fwdX = Math.sin(mark.heading);
      const fwdZ = Math.cos(mark.heading);
      const sideX = Math.cos(mark.heading);
      const sideZ = -Math.sin(mark.heading);
      const halfLength = mark.length * 0.5;
      const halfWidth = mark.width * 0.5;
      const y = mark.y;
      const fade = mark.alpha * (1 - (mark.age / mark.life) * config.markFade);
      const verts = [
        [-fwdX * halfLength - sideX * halfWidth, -fwdZ * halfLength - sideZ * halfWidth],
        [-fwdX * halfLength + sideX * halfWidth, -fwdZ * halfLength + sideZ * halfWidth],
        [fwdX * halfLength + sideX * halfWidth, fwdZ * halfLength + sideZ * halfWidth],
        [fwdX * halfLength - sideX * halfWidth, fwdZ * halfLength - sideZ * halfWidth],
      ];
      for (let k = 0; k < 4; k++) {
        const idx = (i * 4 + k) * 3;
        this.markPositions[idx + 0] = mark.x + verts[k][0];
        this.markPositions[idx + 1] = y;
        this.markPositions[idx + 2] = mark.z + verts[k][1];
        this.markAlphas[i * 4 + k] = fade;
      }
    }

    (this.markGeom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.markGeom.getAttribute('aAlpha') as THREE.BufferAttribute).needsUpdate = true;
    this.markGeom.setDrawRange(0, count * 6);
  }

  private updateSparkGeometry(): void {
    const config = this.config;
    const count = this.sparkData.length;
    for (let i = 0; i < count; i++) {
      const p = this.sparkData[i];
      const t = p.age / p.life;
      const speed = Math.hypot(p.vx, p.vy, p.vz) || 1;
      const tailLength = p.size * (config.sparkTailBase + speed * config.sparkTailSpeed);
      const tailX = p.x - (p.vx / speed) * tailLength;
      const tailY = p.y - (p.vy / speed) * tailLength;
      const tailZ = p.z - (p.vz / speed) * tailLength;
      const alpha = p.alpha * (1 - t);
      const heat = THREE.MathUtils.clamp(p.heat, 0, 1);
      const base = i * 6;
      this.sparkPositions[base + 0] = p.x;
      this.sparkPositions[base + 1] = p.y;
      this.sparkPositions[base + 2] = p.z;
      this.sparkPositions[base + 3] = tailX;
      this.sparkPositions[base + 4] = tailY;
      this.sparkPositions[base + 5] = tailZ;
      this.sparkAlphas[i * 2 + 0] = alpha;
      this.sparkAlphas[i * 2 + 1] = alpha * 0.1;
      this.sparkHeats[i * 2 + 0] = heat;
      this.sparkHeats[i * 2 + 1] = heat * 0.35;
    }

    (this.sparkGeom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.sparkGeom.getAttribute('aAlpha') as THREE.BufferAttribute).needsUpdate = true;
    (this.sparkGeom.getAttribute('aHeat') as THREE.BufferAttribute).needsUpdate = true;
    this.sparkGeom.setDrawRange(0, count * 2);
  }
}

const MAX_GROUND_STREAKS = 96;

interface GroundStreak {
  age: number;
  life: number;
  /** Streak head (closer to racer) and tail (behind). */
  hx: number;
  hz: number;
  tx: number;
  tz: number;
  alpha: number;
}

/**
 * Velocity-stretched ground streaks under the racer. Each streak is a single
 * line segment from a head point to a tail point dragged backward along the
 * racer's velocity vector. Spawn rate and stretch length both scale with
 * speed, so at low speed they're sparse and short, at top speed they're a
 * dense rush past the camera.
 *
 * Single THREE.LineSegments call. Buffer is upper-bounded by `MAX_GROUND_STREAKS`.
 * No texture; the per-vertex alpha attribute fades the tail so each streak
 * reads as a tapered smear.
 */
export class GroundStreaks {
  readonly lines: THREE.LineSegments;
  private readonly geom: THREE.BufferGeometry;
  private readonly positions: Float32Array;
  private readonly alphas: Float32Array;
  private readonly config: GroundStreaksConfig;
  private readonly streaks: GroundStreak[] = [];
  private spawnAccumulator = 0;

  constructor(config?: PartialConfig<GroundStreaksConfig>, rendererMode: RendererMode = 'webgl2') {
    this.config = mergeParticleConfig(DEFAULT_PARTICLES_CONFIG.groundStreaks, config);
    this.geom = new THREE.BufferGeometry();
    // 2 verts per streak.
    this.positions = new Float32Array(MAX_GROUND_STREAKS * 2 * 3);
    this.alphas = new Float32Array(MAX_GROUND_STREAKS * 2);
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geom.setAttribute('aAlpha', new THREE.BufferAttribute(this.alphas, 1));
    this.geom.setDrawRange(0, 0);

    const mat = rendererMode === 'webgpu'
      ? makeWebGPUGroundStreakMaterial()
      : new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexShader: `
          attribute float aAlpha;
          varying float vAlpha;
          void main() {
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            vAlpha = aAlpha;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          void main() {
            gl_FragColor = vec4(0.95, 0.92, 0.82, vAlpha);
          }
        `,
      });

    this.lines = new THREE.LineSegments(this.geom, mat);
    this.lines.frustumCulled = false;
    this.lines.renderOrder = 5;
  }

  /**
   * Drive from the racer's actual velocity and a 0..1 speed-band gate so the
   * streaks ramp in over a tunable speed range (e.g. smoothstep above 30% of
   * maxSpeed). `groundY` is sampled track height at the racer's position.
   */
  spawn(
    racerPos: THREE.Vector3,
    velocity: THREE.Vector3,
    groundY: number,
    speedFactor: number,
    dt: number,
  ): void {
    if (speedFactor <= 0.01) return;
    const config = this.config;
    const speed = Math.hypot(velocity.x, velocity.z);
    if (speed < config.minSpeed) return;

    // Spawn rate ramps with speed band. Cap at ~120/s so we don't drown the buffer.
    const rate = config.spawnRateBase + config.spawnRateSpeed * speedFactor;
    this.spawnAccumulator += rate * dt;
    while (this.spawnAccumulator >= 1 && this.streaks.length < MAX_GROUND_STREAKS) {
      this.spawnAccumulator -= 1;
      const dirX = velocity.x / speed;
      const dirZ = velocity.z / speed;
      // Spread streaks laterally around the racer center within ~3m.
      const sideX = -dirZ;
      const sideZ = dirX;
      const lateral = (Math.random() - 0.5) * config.lateralSpread;
      const ahead = (Math.random() - 0.5) * config.aheadSpread;
      const hx = racerPos.x + sideX * lateral + dirX * ahead;
      const hz = racerPos.z + sideZ * lateral + dirZ * ahead;
      // Tail length scales with speed so streaks elongate as you accelerate.
      const length = config.lengthBase + speedFactor * config.lengthSpeed + Math.random() * config.lengthJitter;
      this.streaks.push({
        age: 0,
        life: config.lifetimeBase + speedFactor * config.lifetimeSpeed,
        hx,
        hz,
        tx: hx - dirX * length,
        tz: hz - dirZ * length,
        alpha: config.opacityBase + speedFactor * config.opacitySpeed,
      });
      void groundY; // groundY consumed below in update for height sync.
    }
  }

  /**
   * Updates per-frame state. `groundYAt` samples track height at any (x,z)
   * so streaks sit just above the asphalt even on banked sections.
   */
  update(dt: number, groundYAt: (x: number, z: number) => number): void {
    const config = this.config;
    for (let i = this.streaks.length - 1; i >= 0; i--) {
      const s = this.streaks[i];
      s.age += dt;
      if (s.age >= s.life) {
        const last = this.streaks.length - 1;
        if (i !== last) this.streaks[i] = this.streaks[last];
        this.streaks.pop();
      }
    }

    const count = this.streaks.length;
    for (let i = 0; i < count; i++) {
      const s = this.streaks[i];
      const t = s.age / s.life;
      const yHead = groundYAt(s.hx, s.hz) + config.headYOffset;
      const yTail = groundYAt(s.tx, s.tz) + config.tailYOffset;
      this.positions[i * 6 + 0] = s.hx;
      this.positions[i * 6 + 1] = yHead;
      this.positions[i * 6 + 2] = s.hz;
      this.positions[i * 6 + 3] = s.tx;
      this.positions[i * 6 + 4] = yTail;
      this.positions[i * 6 + 5] = s.tz;
      // Head bright, tail fades. Whole streak fades over its life.
      const baseAlpha = s.alpha * (1 - t);
      this.alphas[i * 2 + 0] = baseAlpha;
      this.alphas[i * 2 + 1] = baseAlpha * config.tailOpacity;
    }

    (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.geom.getAttribute('aAlpha') as THREE.BufferAttribute).needsUpdate = true;
    this.geom.setDrawRange(0, count * 2);
  }

  clear(): void {
    this.streaks.length = 0;
    this.spawnAccumulator = 0;
    this.geom.setDrawRange(0, 0);
  }
}
